<script lang="ts" setup>
import { ToccoEventItem, ToccoSeminarModuleItem } from '~/api/drupal'

defineProps<{
  item: ToccoEventItem
  modules: ToccoSeminarModuleItem[]
}>()

defineEmits<{
  openModule: [id: string]
}>()

const { t } = useI18n()

const expanded = ref(false)
</script>

<template>
  <li class="seminar-row">
    <div class="header">
      <NuxtLink :to="item.url" class="title-link">
        <h3 class="title text-medium">{{ item.title }}</h3>
      </NuxtLink>
      <div class="actions">
        <button
          v-if="modules.length"
          class="modules-toggle label"
          :aria-expanded="expanded"
          :aria-controls="`seminar-modules-${item.entityId}`"
          @click="expanded = !expanded"
        >
          {{ t('tocco.eventList.modules', modules.length) }}
          <Icon :name="expanded ? 'mini-minus-small' : 'mini-plus-small'" />
        </button>
        <NuxtLink :to="item.url" :title="item.title" class="arrow-link">
          <ButtonIcon />
        </NuxtLink>
      </div>
    </div>
    <div v-if="expanded && modules.length" :id="`seminar-modules-${item.entityId}`" class="modules">
      <ToccoModuleCard
        v-for="module in modules"
        :key="module.id"
        :title="module.title"
        :duration-days="module.durationDays"
        :optional="module.optional"
        @click="$emit('openModule', module.id)"
      />
    </div>
  </li>
</template>

<style lang="scss" scoped>
.seminar-row {
  border-bottom: 1px solid var(--c-line-light);
}

.header {
  display: grid;
  grid-template-columns: 100%;
  align-items: center;
  gap: var(--gap);

  // Row padding lives on the header so the expanded module band runs
  // flush to the divider, as on the listing artboard.
  @include fluid(padding-block, 30px, 32px);

  @include breakpoint(tl) {
    grid-template-columns: 1fr auto;
  }
}

.title {
  margin-bottom: 0;
  color: var(--c-primary);
}

.actions {
  display: flex;
  align-items: center;
  gap: var(--gap);

  @include breakpoint(tl) {
    justify-self: end;
  }
}

.modules-toggle {
  display: flex;
  align-items: center;
  gap: 8px;

  height: var(--h);
  padding-inline: 20px;

  color: var(--c-primary);
  border: 1px solid rgba(var(--c-blue-100-rgb), 0.6);

  @include transitions(color, border-color, box-shadow);
  @include fluid-ts-ds(--h, 38px, 54px);

  @include hover {
    color: var(--c-blue-rollover);
    border-color: var(--c-blue-rollover);
    box-shadow: inset 0 0 0 1px var(--c-blue-rollover);
  }

  &[aria-expanded='true'] {
    color: var(--c-blue-rollover);
    border-color: var(--c-blue-rollover);
    box-shadow: inset 0 0 0 1px var(--c-blue-rollover);
  }
}

.arrow-link {
  display: flex;
}

.modules {
  display: grid;
  grid-template-columns: 100%;
  gap: 12px;

  padding: 32px;

  background: var(--c-background-light);

  @include breakpoint(ts) {
    grid-template-columns: 1fr 1fr;
  }
  @include breakpoint(tl) {
    grid-template-columns: 1fr 1fr 1fr;
  }
}
</style>
